// src/pages/Dealer/ViewDealerVisits.jsx
import { useState, useEffect, useCallback } from 'react';
import dealerVisitService from '../../services/dealerVisit.service';
import Toast from '../../components/Toast/Toast';
import './ViewDealerVisits.css';

const today = () => new Date().toISOString().split('T')[0];

const formatDateTime = (d) => d
  ? new Date(d).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    })
  : '—';

const ViewDealerVisits = () => {
  const [visits,     setVisits]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [toast,      setToast]      = useState(null);
  const [search,     setSearch]     = useState('');
  const [pagination, setPagination] = useState({ page:1, limit:20, total:0, totalPages:0 });

  const [filters, setFilters] = useState({
    fromDate: today(),
    toDate:   today(),
  });

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchVisits = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dealerVisitService.getAllVisits(
        filters.fromDate,
        filters.toDate,
        debouncedSearch,
        pagination.page,
        pagination.limit
      );
      if (res.success) {
        setVisits(res.data.visits || []);
        setPagination(prev => ({ ...prev, ...res.data.pagination }));
      }
    } catch {
      setToast({ message: 'Failed to fetch dealer visits', type: 'error' });
    } finally { setLoading(false); }
  }, [filters, debouncedSearch, pagination.page]);

  useEffect(() => { fetchVisits(); }, [filters, debouncedSearch, pagination.page]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const setQuickDate = (type) => {
    const now = new Date();
    if (type === 'today') {
      setFilters({ fromDate: today(), toDate: today() });
    } else if (type === 'week') {
      const from = new Date(now);
      from.setDate(now.getDate() - 6);
      setFilters({ fromDate: from.toISOString().split('T')[0], toDate: today() });
    } else if (type === 'month') {
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      setFilters({ fromDate: from.toISOString().split('T')[0], toDate: today() });
    } else if (type === 'all') {
      setFilters({ fromDate: '', toDate: '' });
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleExport = () => {
    if (!visits.length) return;
    const headers = ['Dealer','Owner','Mobile','State','Sales Officer','Code','Visit Time','Location','Remark'];
    const rows = visits.map(v => [
      v.dealerShopName || '',
      v.ownerName      || '',
      v.dealerMobile   || '',
      v.dealerState    || '',
      `${v.soFirstName || ''} ${v.soLastName || ''}`.trim(),
      v.soCode         || '',
      formatDateTime(v.visitTime),
      v.visitLatitude  ? `${parseFloat(v.visitLatitude).toFixed(5)}, ${parseFloat(v.visitLongitude).toFixed(5)}` : '',
      (v.remark || '').replace(/,/g, ';'),
    ]);
    const csv  = [headers, ...rows].map(r => r.join(',')).join('\n');
    const a    = document.createElement('a');
    a.href     = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `dealer-visits-${filters.fromDate||'all'}-to-${filters.toDate||'all'}.csv`;
    a.click();
  };

  return (
    <div className="dvv-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Breadcrumb */}
      <nav className="dvv-breadcrumb">
        <a href="/dashboard/overview">Dashboard</a>
        <span>›</span><span>Dealer</span>
        <span>›</span><span>Dealer Visits</span>
      </nav>

      {/* Header */}
      <div className="dvv-header">
        <div>
          <h4 className="dvv-title">Dealer Visits</h4>
          <p className="dvv-subtitle">All dealer visits by your sales team</p>
        </div>
        <button className="dvv-export-btn" onClick={handleExport} disabled={!visits.length}>
          ⬇ Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="dvv-filters">
        <div className="dvv-filter-row">
          <div className="dvv-filter-group">
            <label className="dvv-filter-label">From Date</label>
            <input type="date" name="fromDate" className="dvv-date-input"
              value={filters.fromDate} onChange={handleFilterChange} max={today()} />
          </div>
          <div className="dvv-filter-group">
            <label className="dvv-filter-label">To Date</label>
            <input type="date" name="toDate" className="dvv-date-input"
              value={filters.toDate} onChange={handleFilterChange} max={today()} />
          </div>
          <div className="dvv-quick-dates">
            <button className="dvv-quick-btn" onClick={() => setQuickDate('today')}>Today</button>
            <button className="dvv-quick-btn" onClick={() => setQuickDate('week')}>This Week</button>
            <button className="dvv-quick-btn" onClick={() => setQuickDate('month')}>This Month</button>
            <button className="dvv-quick-btn" onClick={() => setQuickDate('all')}>All Time</button>
          </div>
        </div>
        <div className="dvv-search-row">
          <input type="text" className="dvv-search-input"
            placeholder="Search by dealer name, owner, sales officer..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
          />
          <span>🔍</span>
        </div>
      </div>

      {/* Summary */}
      <div className="dvv-summary">
        <div className="dvv-summary-card" style={{ borderLeft: '4px solid #4caf50', background: '#e8f5e9' }}>
          <p className="dvv-summary-count" style={{ color: '#2e7d32' }}>{pagination.total}</p>
          <p className="dvv-summary-label">Total Visits</p>
        </div>
        <div className="dvv-summary-card" style={{ borderLeft: '4px solid #1565c0', background: '#e3f2fd' }}>
          <p className="dvv-summary-count" style={{ color: '#1565c0' }}>
            {new Set(visits.map(v => v.dealerId)).size}
          </p>
          <p className="dvv-summary-label">Unique Dealers</p>
        </div>
        <div className="dvv-summary-card" style={{ borderLeft: '4px solid #6a1b9a', background: '#f3e5f5' }}>
          <p className="dvv-summary-count" style={{ color: '#6a1b9a' }}>
            {new Set(visits.map(v => v.userId)).size}
          </p>
          <p className="dvv-summary-label">Sales Officers</p>
        </div>
      </div>

      {/* Table */}
      <div className="dvv-table-container">
        {loading ? (
          <div className="dvv-loading">Loading dealer visits...</div>
        ) : visits.length === 0 ? (
          <div className="dvv-empty">
            <p style={{ fontSize: 32 }}>🏪</p>
            <p>No dealer visits found for the selected period</p>
          </div>
        ) : (
          <table className="dvv-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Dealer</th>
                <th>Owner</th>
                <th>Sales Officer</th>
                <th>Visit Time</th>
                <th>Location</th>
                <th>Remark</th>
              </tr>
            </thead>
            <tbody>
              {visits.map((v, idx) => (
                <tr key={v.id}>
                  <td style={{ fontWeight: 600, color: '#4caf50' }}>
                    {(pagination.page - 1) * pagination.limit + idx + 1}
                  </td>
                  <td>
                    <div className="dvv-dealer-cell">
                      <span className="dvv-dealer-name">{v.dealerShopName || '—'}</span>
                      {v.dealerMobile && (
                        <span className="dvv-dealer-mobile">📱 {v.dealerMobile}</span>
                      )}
                      {v.dealerState && (
                        <span className="dvv-dealer-state">{v.dealerState}</span>
                      )}
                    </div>
                  </td>
                  <td style={{ fontSize: 13 }}>{v.ownerName || '—'}</td>
                  <td>
                    <div className="dvv-so-cell">
                      <div className="dvv-so-avatar">
                        {v.soFirstName?.[0]}{v.soLastName?.[0]}
                      </div>
                      <div>
                        <p className="dvv-so-name">{v.soFirstName} {v.soLastName}</p>
                        {v.soCode && <p className="dvv-so-code">{v.soCode}</p>}
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                    {formatDateTime(v.visitTime)}
                  </td>
                  <td>
                    {v.visitLatitude ? (
                      <a
                        href={`https://maps.google.com/maps?q=${v.visitLatitude},${v.visitLongitude}`}
                        target="_blank" rel="noreferrer"
                        className="dvv-map-link"
                      >
                        📍 {parseFloat(v.visitLatitude).toFixed(4)},
                        {parseFloat(v.visitLongitude).toFixed(4)}
                      </a>
                    ) : (
                      <span style={{ color: '#adb5bd', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: '#495057', maxWidth: 200 }}>
                    {v.remark || <span style={{ color: '#adb5bd' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="dvv-pagination">
          <button className="dvv-page-btn" disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}>
            ← Previous
          </button>
          <span className="dvv-page-info">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
          </span>
          <button className="dvv-page-btn" disabled={pagination.page >= pagination.totalPages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default ViewDealerVisits;